import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAgents } from '../hooks/useDashboard';
import BookingsTab from '../components/BookingsTab';

const Bookings: React.FC = () => {
  const { user } = useAuth();
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

  // React Query hook
  const { data: agentsData, isLoading: agentsLoading } = useAgents();
  const agents = useMemo(() => agentsData?.agents || [], [agentsData?.agents]);

  // Auto-select first agent when agents load
  React.useEffect(() => {
    if (agents.length > 0 && !selectedAgent) {
      setSelectedAgent(agents[0].id);
    }
    // If selected agent no longer exists, select first available
    if (selectedAgent && agents.length > 0 && !agents.find(agent => agent.id === selectedAgent)) {
      setSelectedAgent(agents[0].id);
    }
    // Clear selection if no agents
    if (agents.length === 0 && selectedAgent) {
      setSelectedAgent(null);
    }
  }, [agents, selectedAgent]);

  if (agentsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading your agents..." />
      </div>
    );
  }

  if (!user?.hasActiveSubscription && !user?.isInGracePeriod) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Bookings Unavailable</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">Subscribe to Svarra to access booking management</p>
          {/* BETA MODE: Subscription disabled
          <Link
            to="/settings?tab=billing"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Subscribe Now
          </Link> */}
          <button
            disabled={true}
            className="inline-flex items-center px-6 py-3 text-base font-medium rounded-lg text-white bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60"
          >
            <span className="flex items-center">
              Coming Soon
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          </button>
        </div>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Bookings
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Manage all your appointments in one place
            </p>
          </div>

          {/* No agents message */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No agents yet
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Create your first AI agent to start receiving bookings
              </p>
              <Link
                to="/dashboard"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:shadow-lg transition-all"
              >
                Create Your First Agent
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Bookings
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                Manage all your appointments in one place
              </p>
            </div>

            {/* Agent Selector */}
            {agents.length > 1 && (
              <div className="mt-4 sm:mt-0">
                <select
                  value={selectedAgent || ''}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="block w-full sm:w-64 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.businessName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Show business name if only one agent */}
          {agents.length === 1 && selectedAgent && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Viewing bookings for: <span className="font-medium text-gray-700 dark:text-gray-300">{agents.find(a => a.id === selectedAgent)?.businessName}</span>
            </p>
          )}
        </div>

        {/* Bookings Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-6">
          {selectedAgent ? (
            <BookingsTab agentHandle={selectedAgent} />
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-300">Please select an agent to view bookings</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Bookings;
